import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
	CreateFileVisualizationBody,
	CreateFileVisualizationResponse,
	CreateUrlVisualizationBody,
	RealtimeMessage,
	UpdateVisualizationBody,
	VisualizationListResponse,
	VisualizationResponse,
} from "@zplanem/responses"
import { useCallback } from "react"
import { apiFetch, uploadToCloudinary } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { usePropertyChannel } from "./use-property-channel"

function basePath(propertyId: string, projectId: string): string {
	return `/api/common/properties/${propertyId}/projects/${projectId}/visualizations`
}

/** Ready visualizations for a project (with short-lived signed URLs). */
export function useVisualizations(propertyId: string, projectId: string) {
	return useQuery({
		queryKey: queryKeys.visualizations(propertyId, projectId),
		queryFn: () =>
			apiFetch<VisualizationListResponse>(basePath(propertyId, projectId)).then(
				(r) => r.visualizations,
			),
		enabled: !!propertyId && !!projectId,
	})
}

/**
 * Create / delete / edit mutations for a project's visualizations. All of them
 * invalidate the project's list except `uploadFile`, whose caller batches
 * several files and invalidates once at the end — hence the exported
 * `invalidate`. The file upload runs the full create →
 * direct-Cloudinary-upload → confirm flow.
 */
export function useVisualizationMutations(
	propertyId: string,
	projectId: string,
) {
	const qc = useQueryClient()
	const path = basePath(propertyId, projectId)
	const invalidate = () =>
		qc.invalidateQueries({
			queryKey: queryKeys.visualizations(propertyId, projectId),
		})

	const uploadFile = useMutation({
		mutationFn: async (input: {
			file: File
			title?: string
			description?: string
		}) => {
			const body: CreateFileVisualizationBody = {
				contentType: input.file.type,
				filename: input.file.name,
				declaredSize: input.file.size,
				title: input.title,
				description: input.description,
			}
			const created = await apiFetch<CreateFileVisualizationResponse>(path, {
				method: "POST",
				body: JSON.stringify(body),
			})
			await uploadToCloudinary(created.upload, input.file)
			return apiFetch<VisualizationResponse>(
				`${path}/${created.visualizationId}/confirm`,
				{ method: "POST" },
			)
		},
		// Deliberately no `onSuccess: invalidate`: the add modal uploads a
		// batch and refetches the list once when the batch settles, instead
		// of once per file.
	})

	const addUrl = useMutation({
		mutationFn: (body: CreateUrlVisualizationBody) =>
			apiFetch<VisualizationResponse>(`${path}/url`, {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: invalidate,
	})

	const remove = useMutation({
		mutationFn: (id: string) =>
			apiFetch<{ deleted: true }>(`${path}/${id}`, { method: "DELETE" }),
		onSuccess: invalidate,
	})

	const update = useMutation({
		mutationFn: (input: { id: string } & UpdateVisualizationBody) =>
			apiFetch<VisualizationResponse>(`${path}/${input.id}`, {
				method: "PATCH",
				body: JSON.stringify({
					title: input.title,
					description: input.description,
				}),
			}),
		onSuccess: invalidate,
	})

	return { uploadFile, addUrl, remove, update, invalidate }
}

/**
 * Subscribe to the property channel and refetch the project's visualizations
 * whenever ANY subscriber's create/confirm/edit/delete/enrich fires the
 * `visualization.changed` event — so the grid stays live for everyone viewing
 * it (and reflects the async URL enrichment when it lands).
 */
export function useVisualizationsRealtime(
	propertyId: string,
	projectId: string,
) {
	const queryClient = useQueryClient()
	const onMessage = useCallback(
		(msg: RealtimeMessage) => {
			if (
				msg.event === "visualization.changed" &&
				msg.payload.propertyId === propertyId &&
				msg.payload.projectId === projectId
			) {
				queryClient.invalidateQueries({
					queryKey: queryKeys.visualizations(propertyId, projectId),
				})
			}
		},
		[queryClient, propertyId, projectId],
	)
	usePropertyChannel(propertyId, onMessage)
}
